// Standard ICS geologic timescale, simplified to period-level for display.
// Periods that have well-known sub-epochs (Cambrian through Cretaceous) get
// an Early/Middle/Late qualifier computed by dividing that period's span
// into thirds — this is a readable approximation, not official sub-epoch
// boundaries (those vary in length and aren't even thirds). Periods that
// ARE epochs already (Paleocene, Eocene, etc.) are shown as-is.
export const PERIODS = [
  // ─────────────────────────────────────────────
  // PRECAMBRIAN
  // ─────────────────────────────────────────────
  {
    name: 'Cryogenian',
    era: 'Precambrian',
    start: 750,
    end: 635,
    subdivide: false,
  },
  {
    name: 'Ediacaran',
    era: 'Precambrian',
    start: 635,
    end: 541,
    subdivide: false,
  },

  // ─────────────────────────────────────────────
  // PALEOZOIC
  // ─────────────────────────────────────────────
  {
    name: 'Cambrian',
    era: 'Paleozoic',
    start: 541,
    end: 485.4,
    subdivide: true,
  },
  {
    name: 'Ordovician',
    era: 'Paleozoic',
    start: 485.4,
    end: 443.8,
    subdivide: true,
  },
  {
    name: 'Silurian',
    era: 'Paleozoic',
    start: 443.8,
    end: 419.2,
    subdivide: true,
  },
  {
    name: 'Devonian',
    era: 'Paleozoic',
    start: 419.2,
    end: 358.9,
    subdivide: true,
  },
  {
    name: 'Carboniferous',
    era: 'Paleozoic',
    start: 358.9,
    end: 298.9,
    subdivide: true,
  },
  {
    name: 'Permian',
    era: 'Paleozoic',
    start: 298.9,
    end: 251.9,
    subdivide: true,
  },

  // ─────────────────────────────────────────────
  // MESOZOIC
  // ─────────────────────────────────────────────
  {
    name: 'Triassic',
    era: 'Mesozoic',
    start: 251.9,
    end: 201.3,
    subdivide: true,
  },
  {
    name: 'Jurassic',
    era: 'Mesozoic',
    start: 201.3,
    end: 145,
    subdivide: true,
  },
  {
    name: 'Cretaceous',
    era: 'Mesozoic',
    start: 145,
    end: 66,
    subdivide: true,
  },

  // ─────────────────────────────────────────────
  // CENOZOIC
  // ─────────────────────────────────────────────
  {
    name: 'Paleocene',
    era: 'Cenozoic',
    start: 66,
    end: 56,
    subdivide: false,
  },
  {
    name: 'Eocene',
    era: 'Cenozoic',
    start: 56,
    end: 33.9,
    subdivide: false,
  },
  {
    name: 'Oligocene',
    era: 'Cenozoic',
    start: 33.9,
    end: 23,
    subdivide: false,
  },
  {
    name: 'Miocene',
    era: 'Cenozoic',
    start: 23,
    end: 5.3,
    subdivide: false,
  },
  {
    name: 'Pliocene',
    era: 'Cenozoic',
    start: 5.3,
    end: 2.58,
    subdivide: false,
  },
  {
    name: 'Pleistocene',
    era: 'Cenozoic',
    start: 2.58,
    end: 0.0117,
    subdivide: false,
  },
];

export const ERA_COLORS = {
  Precambrian: 'rgba(83, 104, 120, 0.18)',
  Paleozoic: 'rgba(107, 142, 90, 0.18)',
  Mesozoic: 'hsla(324, 30%, 51%, 0.18)',
  Cenozoic: 'rgba(210, 154, 90, 0.18)',
};

export const PERIOD_COLORS = {
  Cryogenian: ERA_COLORS.Precambrian,
  Ediacaran: ERA_COLORS.Precambrian,

  Cambrian: ERA_COLORS.Paleozoic,
  Ordovician: ERA_COLORS.Paleozoic,
  Silurian: ERA_COLORS.Paleozoic,
  Devonian: ERA_COLORS.Paleozoic,
  Carboniferous: ERA_COLORS.Paleozoic,
  Permian: ERA_COLORS.Paleozoic,

  Triassic: ERA_COLORS.Mesozoic,
  Jurassic: ERA_COLORS.Mesozoic,
  Cretaceous: ERA_COLORS.Mesozoic,

  Paleocene: ERA_COLORS.Cenozoic,
  Eocene: ERA_COLORS.Cenozoic,
  Oligocene: ERA_COLORS.Cenozoic,
  Miocene: ERA_COLORS.Cenozoic,
  Pliocene: ERA_COLORS.Cenozoic,
  Pleistocene: ERA_COLORS.Cenozoic,
};

export const PERIOD_ABBR = {
  Cryogenian: 'Cg', Ediacaran: 'Ed', Cambrian: 'Cm', Ordovician: 'O',
  Silurian: 'S', Devonian: 'D', Carboniferous: 'C', Permian: 'P',
  Triassic: 'Tr', Jurassic: 'J', Cretaceous: 'K', Paleocene: 'Pg',
  Eocene: 'E', Oligocene: 'Ol', Miocene: 'Mi', Pliocene: 'Pli', Pleistocene: 'Ple',
};

export const ERA_ABBR = {
  Precambrian: 'Pre',
  Paleozoic: 'Paleozoic',
  Mesozoic: 'Mesozoic',
  Cenozoic: 'Cenozoic',
};


export function getEraLabel(ageMa) {
  if (ageMa <= 0.0117) return 'Present Day';

  const period = PERIODS.find((p) => ageMa <= p.start && ageMa > p.end);
  if (!period) return `${ageMa} Ma`; // fallback, shouldn't normally hit

  if (!period.subdivide) return period.name;

  const span = period.start - period.end;
  const third = span / 3;
  if (ageMa > period.start - third) return `Early ${period.name}`;
  if (ageMa > period.end + third) return `Middle ${period.name}`;
  return `Late ${period.name}`;
}


function getPeriodForAge(ageMa) {
  if (ageMa <= 0.0117) return PERIODS[PERIODS.length - 1]; // bucket "present" into Pleistocene
  return PERIODS.find((p) => ageMa <= p.start && ageMa > p.end) || null;
}

/**
 * Groups consecutive frames (PALEO_EARTH_FRAMES is oldest-first) into
 * contiguous same-period runs, and expresses each run's position/width as
 * a PERCENT OF FRAME COUNT — the same scale the drag handle and marker
 * already use (index / lastIndex). This is what makes the colored strip
 * actually line up with where dragging lands, instead of two scales
 * silently disagreeing with each other.
 */
// In GeologicEras.js, replace the return statement of buildFrameBasedSegments:

export function buildFrameBasedSegments(frames, minWidthPct = 8) {
  const total = frames.length;
  const runs = [];
  let currentName = null;
  let startIndex = 0;

  frames.forEach((frame, i) => {
    const period = getPeriodForAge(frame.ageMa);
    const era = period ? period.era : 'Unknown';
    if (currentName === null) {
      currentName = era;
      startIndex = i;
    } else if (era !== currentName) {
      runs.push({ name: currentName, startIndex, endIndex: i - 1 });
      currentName = era;
      startIndex = i;
    }
    if (i === frames.length - 1) {
      runs.push({ name: currentName, startIndex, endIndex: i });
    }
  });

  const raw = runs.map((run) => ({
    ...run,
    widthPct: ((run.endIndex - run.startIndex + 1) / total) * 100,
  }));

  // Enforce a minimum visible width so the marker (which has its own
  // reserved edge margin) can always physically reach every segment —
  // without this, a segment with very few frames (e.g. only 2 out of 90)
  // can render narrower than the marker's inset, making it permanently
  // unreachable even though its frames are valid, selectable indices.
  const below = raw.filter((s) => s.widthPct < minWidthPct);
  const above = raw.filter((s) => s.widthPct >= minWidthPct);

  const deficit = below.reduce((sum, s) => sum + (minWidthPct - s.widthPct), 0);
  const surplus = above.reduce((sum, s) => sum + s.widthPct, 0);

  const adjusted = raw.map((s) => {
    if (s.widthPct < minWidthPct) return { ...s, widthPct: minWidthPct };
    // Shrink each "above" segment proportional to its share of the total
    // surplus pool, just enough collectively to cover what the "below"
    // segments needed bumped up.
    const shrinkShare = surplus > 0 ? (s.widthPct / surplus) * deficit : 0;
    return { ...s, widthPct: s.widthPct - shrinkShare };
  });

  // Recompute leftPct as a running total, now using the ADJUSTED widths
  let cursor = 0;
  return adjusted.map((s) => {
    const seg = { ...s, leftPct: cursor };
    cursor += s.widthPct;
    return seg;
  });
}